import type { InfiniteData, QueryKey } from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'

import type {
  SocialListPostByProductQuery,
  SocialListPostByProductQueryVariables,
} from '@web/gen/feed-post'
import {
  socialListPostByProductQueryKey,
  socialListPostByProductQueryFetcher,
} from '@web/gen/feed-post'
import type { SocialListPostByProductInput } from '@web/gen/types'

export const ITEMS_PER_PAGE = 10

type PageParam = Pick<SocialListPostByProductInput, 'pageSize' | 'cursor'>

export function useInfiniteSocialListPostByProductQuery(
  input: SocialListPostByProductQueryVariables,
  options?: {
    enabled?: boolean
  },
) {
  return useInfiniteQuery<
    SocialListPostByProductQuery,
    Error,
    InfiniteData<SocialListPostByProductQuery>,
    QueryKey,
    PageParam | undefined
  >({
    initialPageParam: undefined,
    queryKey: [...socialListPostByProductQueryKey(input), input.input],
    queryFn: async ({ pageParam }) => {
      const initialPageInput = input.input ?? {}

      const nextPageInput = {
        pageSize: initialPageInput.pageSize ?? ITEMS_PER_PAGE,
        cursor: pageParam?.cursor ?? initialPageInput.cursor ?? null,
      }

      const fetcher = socialListPostByProductQueryFetcher({
        input: {
          ...input.input,
          ...nextPageInput,
        },
      })

      const data = await fetcher()

      return data
    },
    getNextPageParam: lastPage => {
      const hasMore = Boolean(lastPage?.socialListPostByProduct?.Pagination?.hasNextPage)

      if (!hasMore) {
        return
      }

      const defaultPageSize = input.input?.pageSize ?? ITEMS_PER_PAGE

      return {
        pageSize: defaultPageSize,
        cursor: lastPage?.socialListPostByProduct?.Pagination?.nextPageCursor ?? null,
      }
    },
    ...options,
  })
}
